import { onError } from '@apollo/client/link/error'
import { setContext } from '@apollo/client/link/context'
import {
  CommonUser,
  Errors,
  UserFieldsFragment,
} from '@/graphql/generated/graphql'
import { toast } from '@/hooks/useToast'
import {
  getAccessTokenRefreshedIfPossible,
  setTokens,
  setUser,
  logout,
  getMaintainerAdminSelectedTeam,
} from '@/hooks/useAuth/zustand'
import { ApolloLink } from '@apollo/client'
import { getSelectedEducationLevelType } from '../EducationLevelsContext/educationLevelState'

export const authRequestLink = setContext(async (operation, prevContext) => {
  if (typeof window === 'undefined') return prevContext

  const token = await getAccessTokenRefreshedIfPossible(operation.operationName)
  const authorization = token && `bearer ${token}`
  const hostpath =
    typeof window !== 'undefined' ? window.location.href : 'ServerSide'
  const teamId = getMaintainerAdminSelectedTeam()
  const selectedLevel = getSelectedEducationLevelType()

  return {
    headers: {
      ...prevContext.headers, // keep haeders directly set in query/mutation
      ...(authorization ? { authorization } : {}),
      ...(teamId && authorization ? { teamId } : {}),
      ...(selectedLevel && authorization ? { selectedLevel } : {}),
      hostpath,
      'Apollo-Require-Preflight': 'true',
    },
  }
})

export const errorLink = onError(
  ({ graphQLErrors, operation, networkError, forward, response }) => {
    const translateEachMessage = (message: string): string => {
      if (message === 'Unauthorized') return 'Não autorizado'
      if (message === 'Not authorized') return 'Não autorizado'
      return message
    }

    if (graphQLErrors) {
      const errorMessages = graphQLErrors.map(
        ({ message, locations, path, extensions }) => {
          console.log(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
          )
          console.log({ locations, extensions })
          const responseMessage = extensions?.exception?.response?.message
          const multipleErrors = typeof responseMessage !== 'string'
          const firstError = multipleErrors
            ? responseMessage?.[0]
            : responseMessage
          return translateEachMessage(firstError || message)
        },
      )
      const errorMessagesUnique = errorMessages.filter(function (
        value,
        index,
        array,
      ) {
        return array.indexOf(value) === index
      })

      const showToastError = (() => {
        const handableErrors = graphQLErrors.filter((el) =>
          Object.values(Errors).includes(el.extensions?.code),
        )
        const hasForceLogoutError = handableErrors.find(
          (el) => el.extensions.code === Errors.UserNotAuthorizedForceLogout,
        )
        if (hasForceLogoutError) {
          logout()
          return true
        }

        const allErrorsIsHandable =
          handableErrors.length === graphQLErrors.length

        console.log({ handableErrors, allErrorsIsHandable })

        return !allErrorsIsHandable
      })()

      if (showToastError) {
        toast({
          // title: "Falha no processamento da requisição",
          title: errorMessagesUnique.join(' '),
          variant: 'destructive',
        })
      }
    }

    if (networkError) {
      console.log(`[Network error]: ${networkError}`)
    }

    if (response) {
      // this prevents the function from being canceled in the middle
      // for example: set a loading variable to off
      // ! IF ENABLED: CANT SEE ERRORS AT THE HOOK
      // response.errors = undefined
    }
    forward(operation)
  },
)

export const handleResponseLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    const context = operation.getContext()
    const { response: contextResponse } = context
    const headers = contextResponse?.headers

    if (headers) {
      const access = headers.get('access')
      const refresh = headers.get('refresh')

      if (access && refresh) {
        setTokens({ access, refresh })
        const user: UserFieldsFragment = Object.entries(
          response.data ?? {},
        ).find(([key, value]) => value.__typename === 'CommonUser')?.[1]

        console.log({ user, response })

        if (user && user.id && user.email) setUser({ user })
      }
    }

    return response
  })
})
